import { cachedFetch } from './cachedFetch.js';
const DEFAULT_ERROR = 'Rankings are temporarily unavailable.';

export async function getRankings({ season, signal, force = false }) {
  const normalizedSeason = Number(season);

  if (!Number.isInteger(normalizedSeason)) {
    throw new Error('A rankings season is required.');
  }

  const response = await cachedFetch(
    `/api/rankings?season=${encodeURIComponent(normalizedSeason)}`,
    {
      headers: { Accept: 'application/json' },
      signal,
      force,
    }
  );
  const payload = await readJson(response);

  if (!response.ok) {
    throw new Error(payload?.message || DEFAULT_ERROR);
  }

  return {
    sport: String(payload?.sport || "Women's Volleyball"),
    division: String(payload?.division || 'd1'),
    season: integerOrNull(payload?.season) || normalizedSeason,
    availableSeasons: normalizeSeasons(payload?.availableSeasons),
    poll: normalizePoll(payload?.poll),
    rankings: Array.isArray(payload?.rankings)
      ? payload.rankings.map(normalizeRanking).filter(Boolean)
      : [],
    source: String(payload?.source || '').trim(),
    stale: Boolean(payload?.stale),
    fallback: Boolean(payload?.fallback),
  };
}

function normalizePoll(poll) {
  return {
    id: String(poll?.id || '').trim(),
    name: String(poll?.name || 'AVCA Division I Coaches Poll').trim(),
    label: String(poll?.label || 'Final AVCA rankings').trim(),
    archiveType: String(poll?.archiveType || '').trim(),
    official: Boolean(poll?.official),
    publishedDate: String(poll?.publishedDate || '').trim(),
    throughDate: String(poll?.throughDate || '').trim(),
    nextPollDate: String(poll?.nextPollDate || '').trim(),
    sourceUrl: String(poll?.sourceUrl || '').trim(),
    generatedAt: String(poll?.generatedAt || '').trim(),
    sourceNote: String(poll?.sourceNote || '').trim(),
    rankedTeamCount: integerOrNull(poll?.rankedTeamCount),
    maxRank: integerOrNull(poll?.maxRank) || 25,
  };
}

function normalizeRanking(item) {
  const rank = integerOrNull(item?.rank);
  const team = normalizeTeam(item?.team);

  if (!rank || !team.name || !team.slug) {
    return null;
  }

  return {
    id: String(item?.id || `${item?.season || ''}:${team.slug}`).trim(),
    season: integerOrNull(item?.season),
    rank,
    rankLabel: String(item?.rankLabel || `#${rank}`).trim(),
    tied: Boolean(item?.tied),
    previousRank: integerOrNull(item?.previousRank),
    previousRankLabel: String(item?.previousRankLabel || '').trim(),
    movement: normalizeMovement(item?.movement),
    points: integerOrNull(item?.points),
    firstPlaceVotes: integerOrNull(item?.firstPlaceVotes),
    rankingDate: String(item?.rankingDate || '').trim(),
    rankingSourceType: String(item?.rankingSourceType || '').trim(),
    rankingSourceUrl: String(item?.rankingSourceUrl || '').trim(),
    official: Boolean(item?.official),
    team,
    pollRecord: String(item?.pollRecord || '').trim(),
    record: String(item?.record || item?.finalRecord?.label || '').trim(),
    wins: integerOrNull(item?.wins),
    losses: integerOrNull(item?.losses),
    finalRecord: normalizeRecord(item?.finalRecord),
    recordSource: String(item?.recordSource || '').trim(),
    seasonFinish: normalizeFinish(item?.seasonFinish),
    tournamentTeam: Boolean(item?.tournamentTeam),
  };
}

function normalizeTeam(team) {
  return {
    slug: String(team?.slug || '').trim(),
    name: String(team?.name || '').trim(),
    longName: String(team?.longName || team?.name || '').trim(),
    aliases: Array.isArray(team?.aliases)
      ? team.aliases.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    logo: String(team?.logo || '').trim(),
  };
}

function normalizeMovement(value) {
  const type = ['up', 'down', 'same', 'new'].includes(value?.type)
    ? value.type
    : 'unavailable';

  return {
    type,
    amount: integerOrNull(value?.amount),
    label: String(value?.label || '').trim(),
  };
}

function normalizeRecord(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return {
    wins: integerOrNull(value.wins),
    losses: integerOrNull(value.losses),
    ties: integerOrNull(value.ties) || 0,
    matches: integerOrNull(value.matches),
    label: String(value.label || '').trim(),
    throughDate: String(value.throughDate || '').trim(),
    complete: Boolean(value.complete),
  };
}

function normalizeFinish(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  return {
    code: String(value.code || '').trim(),
    label: String(value.label || '').trim(),
    placement: String(value.placement || '').trim(),
    display: String(value.display || value.label || '').trim(),
    roundReached: String(value.roundReached || '').trim(),
    tournamentTeam: Boolean(value.tournamentTeam),
    result: String(value.result || '').trim(),
    opponentName: String(value.opponentName || '').trim(),
    lastGameDate: String(value.lastGameDate || '').trim(),
    verified: Boolean(value.verified),
    season: integerOrNull(value.season),
  };
}

function normalizeSeasons(values) {
  const seasons = Array.isArray(values)
    ? values.map(integerOrNull).filter(Boolean)
    : [];

  return Array.from(new Set(seasons)).sort((left, right) => right - left);
}

function integerOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
